import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const blank = (userName) => ({
  player_name: "", birth_year: "", position: "", current_team: "",
  rffm_code: "", added_by: userName || "", observations: "",
});

export default function ScoutingAddPlayerDialog({ open, onOpenChange, onCreated }) {
  const { user } = useAuth();
  const [form, setForm] = useState(() => blank(user?.name));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open) setForm((f) => ({ ...f, added_by: f.added_by || user?.name || "" }));
  }, [open, user]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.player_name.trim()) { toast.error("El nombre del jugador es obligatorio"); return; }
    if (!form.added_by.trim()) { toast.error("Indica quién añade al jugador"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/scouting-market/players", {
        player_name: form.player_name.trim(),
        birth_year: form.birth_year ? parseInt(form.birth_year, 10) : null,
        position: form.position.trim(),
        current_team: form.current_team.trim(),
        rffm_code: form.rffm_code.trim(),
        added_by: form.added_by.trim(),
        observations: form.observations.trim(),
      });
      toast.success(data.rffm_job
        ? `${data.player.player_name} creado · informe RFFM en cola (3 temporadas)`
        : `${data.player.player_name} creado`);
      setForm(blank(user?.name));
      onOpenChange(false);
      onCreated?.(data.player);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="add-scouting-player-dialog">
        <DialogHeader>
          <DialogTitle>Añadir jugador al scouting</DialogTitle>
          <DialogDescription className="text-xs">Alta manual de un jugador externo en la base de scouting.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div>
            <Label className="text-xs">Nombre del jugador *</Label>
            <Input value={form.player_name} onChange={(e) => set("player_name", e.target.value)} placeholder="APELLIDOS, NOMBRE" data-testid="add-player-name" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Año de nacimiento</Label>
              <Input type="number" value={form.birth_year} onChange={(e) => set("birth_year", e.target.value)} placeholder="2012" data-testid="add-player-year" />
            </div>
            <div>
              <Label className="text-xs">Posición</Label>
              <Input value={form.position} onChange={(e) => set("position", e.target.value)} placeholder="Delantero" data-testid="add-player-position" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Club actual</Label>
            <Input value={form.current_team} onChange={(e) => set("current_team", e.target.value)} placeholder="Nombre del club" data-testid="add-player-team" />
          </div>
          <div>
            <Label className="text-xs">Código RFFM (opcional)</Label>
            <Input value={form.rffm_code} onChange={(e) => set("rffm_code", e.target.value)} placeholder="p. ej. 14588293" data-testid="add-player-rffm-code" />
            <p className="text-[10px] text-slate-400 mt-1">
              Si lo indicas, se generará automáticamente el informe RFFM con las 3 últimas temporadas.
            </p>
          </div>
          <div>
            <Label className="text-xs">Añadido por *</Label>
            <Input value={form.added_by} onChange={(e) => set("added_by", e.target.value)} placeholder="Nombre de quien lo añade" data-testid="add-player-added-by" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} className="text-white font-bold w-full" style={{ backgroundColor: "#5B2383" }} data-testid="add-player-save">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Crear jugador
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
